import React, { useMemo } from "react";

import {
  Icon,
  TooltipDescriptive,
  Paragraph,
  useColorTokens
} from "@tvg/design-system";

// Utils
import { capitalize, orderBy } from "lodash";
import { formatPostTime, formatPeriod } from "@tvg/formatter/dates";
import { processSurfaceRaceChanges } from "../../ChangesCard/changesProcessor";
// Components
import { ChangeContainer } from "./styled-components";
// Types
import { RaceHeaderDetail } from "../types";

interface RaceChangeIndicatorProps {
  detail: RaceHeaderDetail;
}

const RaceChangeIndicator: React.FC<RaceChangeIndicatorProps> = ({
  detail
}) => {
  const colorTokens = useColorTokens();

  const changesProcessed = useMemo(
    () =>
      processSurfaceRaceChanges({
        [detail.changeKeyword]: detail.changes
      }),
    [detail]
  );

  if (!detail.changed) {
    return null;
  }

  return (
    <TooltipDescriptive
      qaLabel="tooltip-raceChanges"
      placement="bottom-mid"
      layer={1000}
      onAction="hover"
      widthContent="auto"
      body={
        <ChangeContainer>
          {orderBy(changesProcessed, ["date"], ["desc"]).map((change) => (
            <Paragraph
              key={change.date}
              qaLabel="text-change-postTime"
              color={colorTokens.content.onDark}
            >
              <>
                <Paragraph qaLabel="text-date-changes" color="white.900" mr="4">
                  <>
                    {formatPostTime(change.date)} {formatPeriod(change.date)}
                  </>
                </Paragraph>
                <Paragraph
                  qaLabel="text-keyword"
                  mr="4"
                  fontWeight="600"
                  color={colorTokens.content.onDark}
                >
                  {capitalize(detail.changeKeyword)}
                </Paragraph>
                <Paragraph qaLabel="text-setTo" color="white.900">
                  {change.oldValue || "Set to\u00A0"}
                </Paragraph>
                {change.oldValue && change.newValue ? (
                  <Icon
                    name="arrowRight"
                    size="s"
                    mr="4"
                    ml="4"
                    lineColor={colorTokens.content.onDark}
                  />
                ) : null}
                <Paragraph
                  qaLabel="text-change-newValue"
                  color="orange.400"
                  fontWeight="600"
                >
                  {change.newValue}
                </Paragraph>
              </>
            </Paragraph>
          ))}
        </ChangeContainer>
      }
    >
      {detail.changed && (
        <Icon
          qaLabel="icon-raceChanges"
          name="changes"
          size="s"
          mr="4.5"
          ml={4}
        />
      )}
    </TooltipDescriptive>
  );
};

export default RaceChangeIndicator;
